import * as Sentry from "@sentry/react";
import { env } from "@/shared/lib/env";

/**
 * Thin wrapper around the Sentry SDK so the rest of the app never imports
 * `@sentry/react` directly. `crash-reporting.ts` delegates its prod
 * `recordError` / `setUserId` here, and `main.tsx` calls `initSentry()`
 * once at boot.
 *
 * Keeping the SDK behind this seam means:
 *   - tests mock `@/shared/lib/sentry`, never the SDK internals;
 *   - swapping providers later is a single-file change;
 *   - `isSentryEnabled()` lets the crash reporter fall back to
 *     `console.error` when no DSN is configured (e.g. a misconfigured
 *     deploy), so an error is never silently dropped.
 *
 * Resolves TD-02.
 */

let enabled = false;

const URL_BREADCRUMB_KEYS = ["url", "from", "to"] as const;

/**
 * Drop the query string and fragment from the URLs Sentry's default fetch /
 * xhr / navigation breadcrumbs record: admin list searches carry people's
 * names there.
 */
export function scrubBreadcrumb(breadcrumb: Sentry.Breadcrumb): Sentry.Breadcrumb {
  const data = breadcrumb.data;
  if (!data) return breadcrumb;
  const scrubbed: Record<string, unknown> = { ...data };
  for (const key of URL_BREADCRUMB_KEYS) {
    const value = scrubbed[key];
    if (typeof value === "string") scrubbed[key] = value.replace(/[?#].*$/, "");
  }
  return { ...breadcrumb, data: scrubbed };
}

/**
 * Initialise Sentry. No-op in development, when already initialised, or
 * when no DSN is configured. Idempotent — safe to call more than once.
 */
export function initSentry(): void {
  if (enabled) return;
  if (env.VITE_APP_ENV === "development") return;
  if (!env.VITE_SENTRY_DSN) return;

  Sentry.init({
    dsn: env.VITE_SENTRY_DSN,
    environment: env.VITE_APP_ENV,
    // Build-time git sha injected by CI; `undefined` falls back to
    // Sentry's own release detection when absent.
    release: env.VITE_APP_RELEASE || undefined,
    // No performance tracing or session replay yet — error reporting only.
    tracesSampleRate: 0,
    // Never attach IPs / cookies / request bodies. We set the user id
    // explicitly via `setSentryUser` and nothing else.
    sendDefaultPii: false,
    beforeBreadcrumb: scrubBreadcrumb,
  });

  enabled = true;
}

/** Whether `initSentry()` has run and a DSN was present. */
export function isSentryEnabled(): boolean {
  return enabled;
}

/**
 * Report an exception. Context entries become Sentry tags so they're
 * searchable (e.g. `correlationId`, `endpoint`, `status`).
 */
export function captureException(error: Error, context?: Readonly<Record<string, string>>): void {
  Sentry.captureException(error, context ? { tags: { ...context } } : undefined);
}

/** Associate (or clear) the current backend user id with the Sentry scope. */
export function setSentryUser(userId: string | null): void {
  Sentry.setUser(userId ? { id: userId } : null);
}
